import { Edit2, Save, X } from 'lucide-react';
import * as React from 'react';

import { Button } from './Button';
import { Textarea } from './Textarea';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './Tooltip';

import { SafeHtml } from '@/components/SafeHtml';
import { cn } from '@/lib/utils';

const Table = React.forwardRef<
  HTMLTableElement,
  React.HTMLAttributes<HTMLTableElement>
>(({ className, ...props }, ref) => (
  <div className="relative w-full overflow-auto">
    <table
      ref={ref}
      className={cn('w-full caption-bottom text-sm', className)}
      {...props}
    />
  </div>
));
Table.displayName = 'Table';

const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead ref={ref} className={cn('[&_tr]:border-b', className)} {...props} />
));
TableHeader.displayName = 'TableHeader';

const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody
    ref={ref}
    className={cn('[&_tr:last-child]:border-0', className)}
    {...props}
  />
));
TableBody.displayName = 'TableBody';

const TableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn(
      'border-t bg-muted/50 font-medium [&>tr]:last:border-b-0',
      className,
    )}
    {...props}
  />
));
TableFooter.displayName = 'TableFooter';

const TableRow = React.forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement>
>(({ className, ...props }, ref) => (
  <tr
    ref={ref}
    className={cn(
      'border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted',
      className,
    )}
    {...props}
  />
));
TableRow.displayName = 'TableRow';

const TableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <th
    ref={ref}
    className={cn(
      'h-10 px-2 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]',
      className,
    )}
    {...props}
  />
));
TableHead.displayName = 'TableHead';

const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <td
    ref={ref}
    className={cn(
      'p-2 align-middle [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]',
      className,
    )}
    {...props}
  />
));
TableCell.displayName = 'TableCell';

export interface TableCellWithTooltipProps {
  content?: string;
  /** Renders `content` as sanitized rich text (titles/paragraphs). */
  html?: boolean;
  actions?: boolean;
  isSaving?: boolean;
  onSave?: (value: string) => void;
  onSaveAsync?: (value: string) => Promise<void>;
  onCancel?: () => void;
}

const TableCellWithTooltip = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement> & TableCellWithTooltipProps
>(
  (
    {
      className,
      children,
      content,
      html = false,
      isSaving,
      onSave,
      onSaveAsync,
      onCancel,
      actions = false,
      ...props
    },
    ref,
  ) => {
    const [editMode, setEditMode] = React.useState(false);
    const [value, setValue] = React.useState('');

    function handleEditValue() {
      setEditMode(true);
      setValue(content ?? '');
    }

    function handleValueChange(event: React.ChangeEvent<HTMLTextAreaElement>) {
      setValue(event.target.value);
    }

    function handleSaveValue() {
      onSave?.(value);
      setEditMode(false);
      setValue('');
    }

    async function handleSaveValueAsync() {
      await onSaveAsync?.(value);
      setEditMode(false);
      setValue('');
    }

    function handleCancel() {
      setEditMode(false);
      setValue('');
      onCancel?.();
    }

    return (
      <TableCell
        ref={ref}
        className={cn('overflow-hidden', className)}
        {...props}
      >
        {editMode && (
          <div className="flex items-center space-x-2">
            <Textarea
              value={value}
              onChange={handleValueChange}
              rows={html ? 10 : 3}
              className={html ? 'min-w-[320px]' : 'min-w-[180px]'}
              readOnly={isSaving}
            />

            <Button
              size="icon"
              variant="ghost"
              type="button"
              onClick={onSaveAsync ? handleSaveValueAsync : handleSaveValue}
              disabled={isSaving}
            >
              <Save className="h-4 w-4" />
            </Button>

            <Button
              type="button"
              size="icon"
              variant="ghost"
              onClick={handleCancel}
              disabled={isSaving}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        )}

        {!editMode && (
          <div className="group flex items-center justify-between gap-2">
            <TooltipProvider>
              <Tooltip>
                {html && content ? (
                  <>
                    <TooltipTrigger asChild>
                      <div className="max-h-28 w-full overflow-hidden">
                        <SafeHtml html={content} />
                      </div>
                    </TooltipTrigger>

                    <TooltipContent>
                      <SafeHtml
                        html={content}
                        className="max-h-96 max-w-md overflow-auto"
                      />
                    </TooltipContent>
                  </>
                ) : (
                  <>
                    <TooltipTrigger asChild>
                      <span className="line-clamp-3">
                        {content ?? children}
                      </span>
                    </TooltipTrigger>

                    <TooltipContent>
                      <p className="max-w-xs">{content ?? children}</p>
                    </TooltipContent>
                  </>
                )}
              </Tooltip>
            </TooltipProvider>

            {actions && (
              <Button
                size="icon"
                variant="ghost"
                className="opacity-0 group-hover:opacity-100"
                onClick={handleEditValue}
              >
                <Edit2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        )}
      </TableCell>
    );
  },
);
TableCellWithTooltip.displayName = 'TableCellWithTooltip';

const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  React.HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <caption
    ref={ref}
    className={cn('mt-4 text-sm text-muted-foreground', className)}
    {...props}
  />
));
TableCaption.displayName = 'TableCaption';

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCellWithTooltip,
  TableCaption,
};
